import React from 'react';
import PropTypes from 'prop-types';
import { createUseStyles } from 'react-jss';
import { useSurveyTheme, createSurveyStyles } from '@sm/webassets';

const useThemeStyles = createUseStyles(({ breakpoints }) => {
  return {
    layoutFloatLeft: {
      [`@media (min-width: ${breakpoints.sm}px)`]: {
        width: '50%',
        position: 'absolute',
        marginRight: '50%',
        minHeight: '100%',
      },
    },

    layoutFixedRight: {
      [`@media (min-width: ${breakpoints.sm}px)`]: {
        position: 'fixed',
        height: '100%',
        width: '50%',
        left: '50%',
      },
    },
    layoutFloatRight: {
      [`@media (min-width: ${breakpoints.sm}px)`]: {
        width: '50%',
        position: 'absolute',
        marginLeft: '50%',
        minHeight: '100%',
      },
    },
    smLayoutFixedLeft: {
      [`@media (min-width: ${breakpoints.sm}px)`]: {
        position: 'fixed',
        height: '100%',
        width: '50%',
        right: '50%',
      },
    },
    layoutTwothirdFloatLeft: {
      [`@media (min-width: ${breakpoints.sm}px)`]: {
        width: '70%',
        position: 'absolute',
        marginRight: '30%',
        minHeight: '100%',
      },
    },
    layoutTwothirdFixedRight: {
      [`@media (min-width: ${breakpoints.sm}px)`]: {
        position: 'fixed',
        top: 0,
        height: '100%',
        width: '30%',
        left: '70%',
      },
    },
    layoutTwothirdFloatRight: {
      [`@media (min-width: ${breakpoints.sm}px)`]: {
        width: '70%',
        position: 'absolute',
        marginLeft: '30%',
        minHeight: '100%',
      },
    },
    layoutTwothirdFixedLeft: {
      [`@media (min-width: ${breakpoints.sm}px)`]: {
        position: 'fixed',
        top: 0,
        height: '100%',
        width: '30%',
        right: '70%',
      },
    },
    layoutTopBanner: {
      [`@media (min-width: ${breakpoints.sm}px)`]: {
        minHeight: 80,
        display: 'block',
        position: 'relative',
        left: 0,
        top: 0,
        width: '100%',
      },
    },
    layoutContent: {
      [`@media (min-width: ${breakpoints.sm}px)`]: {
        maxWidth: 896,
        border: 'none',
        padding: [0, 40, 80],
        margin: [0, 'auto'],
      },
    },
    layoutContentLeft: {
      [`@media (min-width: ${breakpoints.sm}px)`]: {
        width: '50%',
        marginRight: '50%',
        minHeight: '100%',
      },
    },
    layoutContentRight: {
      [`@media (min-width: ${breakpoints.sm}px)`]: {
        width: '50%',
        marginLeft: '50%',
        minHeight: '100%',
      },
    },
    layoutFull: {
      maxWidth: 896,
      margin: [0, 'auto'],
    },
    layoutRTL: {
      direction: 'rtl',
    },
  };
});

const useStyles = createSurveyStyles(theme => {
  let bgColor = theme?.primaryBackgroundColor;
  if (theme?.surveyPage?.backgroundImage) {
    bgColor = theme?.surveyPage?.overlayColor;
  }
  return {
    layoutBg: {
      background: {
        position: '50% 50%',
        size: 'cover',
        image: theme?.surveyPage?.backgroundImage,
        color: bgColor,
      },
    },
    layoutBgOverlay: {
      backgroundColor: bgColor,
    },
    topBannerContent: {
      backgroundColor: theme?.secondaryBackgroundColor,
    },
    partialLayoutContent: {
      color: theme?.questionColor,
      backgroundColor: theme?.primaryBackgroundColor,
      fontFamily: theme?.questionTitle?.fontFamily,
    },
    fullLayoutContent: {
      color: theme?.questionColor,
      fontFamily: theme?.questionTitle?.fontFamily,
    },
  };
});

const Layout = ({ isRTL, children }) => {
  let grid;
  const sPageTheme = useSurveyTheme();
  const [surveyHeader, ...surveyContent] = children;
  const classes = useStyles();
  const {
    layoutFull,
    layoutFloatLeft,
    layoutTwothirdFloatLeft,
    smLayoutFixedLeft,
    layoutTwothirdFixedLeft,
    layoutFixedRight,
    layoutTwothirdFixedRight,
    layoutFloatRight,
    layoutTwothirdFloatRight,
    layoutContent,
    layoutContentLeft,
    layoutContentRight,
    layoutTopBanner,
    layoutRTL,
  } = useThemeStyles();

  switch (sPageTheme.layout) {
    case 'BACKGROUND_PARTIAL_RIGHT_HALF':
      grid = (
        <>
          <div className={`${classes.partialLayoutContent} ${layoutFloatLeft}`} data-testid="Layout__FloatLeft">
            {children}
          </div>
          <div className={`${classes.layoutBg} ${layoutFixedRight}`} data-testid="Layout__FixedRight" />
        </>
      );
      break;
    case 'BACKGROUND_PARTIAL_RIGHT_THIRD':
      grid = (
        <>
          <div
            className={`${classes.partialLayoutContent} ${layoutTwothirdFloatLeft}`}
            data-testid="Layout__TwoThirdFloatLeft"
          >
            {children}
          </div>
          <div className={`${classes.layoutBg} ${layoutTwothirdFixedRight}`} data-testid="Layout__TwoThirdFixedRight" />
        </>
      );
      break;
    case 'BACKGROUND_PARTIAL_LEFT_HALF':
      grid = (
        <>
          <div className={`${classes.layoutBg} ${smLayoutFixedLeft}`} data-testid="Layout__FixedLeft" />
          <div className={`${classes.partialLayoutContent} ${layoutFloatRight}`} data-testid="Layout__FloatRight">
            {children}
          </div>
        </>
      );
      break;
    case 'BACKGROUND_PARTIAL_LEFT_THIRD':
      grid = (
        <>
          <div className={`${classes.layoutBg} ${layoutTwothirdFixedLeft}`} data-testid="Layout__TwoThirdFixedLeft" />
          <div
            className={`${classes.partialLayoutContent} ${layoutTwothirdFloatRight}`}
            data-testid="Layout__TwoThirdFloatRight"
          >
            {children}
          </div>
        </>
      );
      break;
    case 'BACKGROUND_PARTIAL_TOP_BANNER':
      grid = (
        <>
          <div
            className={`${classes.layoutBg} ${classes.topBannerContent} ${layoutTopBanner}`}
            data-testid="Layout__TopBanner"
          >
            {' '}
            {surveyHeader}
          </div>
          <div className={classes.partialLayoutContent}>
            <div className={layoutFull} data-testid="Layout__Full">
              {surveyContent}
            </div>
          </div>
        </>
      );
      break;
    case 'BACKGROUND_FULL_TEXT_LEFT':
      grid = (
        <div className={classes.layoutBg}>
          <div
            className={`${classes.fullLayoutContent} ${layoutContentLeft} ${layoutContent}`}
            data-testid="Layout__Content"
          >
            {children}
          </div>
        </div>
      );
      break;
    case 'BACKGROUND_FULL_TEXT_RIGHT':
      grid = (
        <div className={classes.layoutBg}>
          <div
            className={`${classes.fullLayoutContent} ${layoutContentRight} ${layoutContent}`}
            data-testid="Layout__Content"
          >
            {children}
          </div>
        </div>
      );
      break;
    default:
      grid = (
        <div className={classes.layoutBg}>
          <div className={classes.layoutBgOverlay}>
            <div className={`${classes.fullLayoutContent} ${layoutContent}`} data-testid="Layout__Content">
              {children}
            </div>
          </div>
        </div>
      );
      break;
  }
  return (
    <div className={isRTL ? layoutRTL : undefined} data-testid="Layout__Container">
      {grid}
    </div>
  );
};

Layout.propTypes = {
  children: PropTypes.node,
  isRTL: PropTypes.bool,
};

Layout.defaultProps = {
  children: null,
  isRTL: false,
};

export default Layout;
